import { ImgHTMLAttributes } from "react";
import watermark from "@/assets/watermark-pawtrait.png";

type ProtectedImageProps = ImgHTMLAttributes<HTMLImageElement> & {
  /**
   * If true, overlays a tiled watermark of the PAWTRAiT logo on top of the image.
   * Use for catalog / gallery / customer-uploaded photos.
   */
  watermarked?: boolean;
};

/**
 * Image wrapper that discourages copying:
 * - Disables right-click context menu
 * - Disables drag-to-save
 * - Disables text/image selection
 * - Optionally overlays a tiled brand watermark
 *
 * Note: This is *deterrence*, not bulletproof protection. A determined user
 * can always screenshot. The watermark makes copies traceable.
 */
const ProtectedImage = ({ watermarked = true, className = "", ...imgProps }: ProtectedImageProps) => {
  const handleContextMenu = (e: React.MouseEvent) => e.preventDefault();
  const handleDragStart = (e: React.DragEvent) => e.preventDefault();

  return (
    <div
      className="relative w-full h-full select-none flex items-center justify-center"
      onContextMenu={handleContextMenu}
      onDragStart={handleDragStart}
    >
      <img
        {...imgProps}
        className={`block pointer-events-none select-none ${className}`}
        draggable={false}
        onContextMenu={handleContextMenu}
        onDragStart={handleDragStart}
        style={{
          WebkitUserSelect: "none",
          userSelect: "none",
          WebkitTouchCallout: "none",
          ...imgProps.style,
        }}
      />
      {watermarked && (
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `url(${watermark})`,
            backgroundRepeat: "repeat",
            backgroundSize: "180px auto",
            opacity: 0.12,
            mixBlendMode: "overlay",
            transform: "rotate(-20deg) scale(1.4)",
            transformOrigin: "center",
          }}
        />
      )}
      {/* Transparent overlay to intercept long-press / right-click on mobile */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-auto"
        onContextMenu={handleContextMenu}
        onDragStart={handleDragStart}
        style={{ background: "transparent" }}
      />
    </div>
  );
};

export default ProtectedImage;
